import React from 'react';
import { Link as RRLink, Navigate } from 'react-router';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { Dialog, DialogContent, Link, Stack, Typography } from '@mui/material';

import type { UserType } from '@shared/auth/index.js';
import type { PatientIntakeStep } from '@shared/patient-intake-status';

import { useAuth } from '../../../../hooks/index.js';
import { getUserTypeValidity, USER_TYPES } from '../../../../utils/index.js';
import { SingleDocumentListItem } from '../../../../components/DocumentManager';
import type { FileUploadType } from '../../../file-upload';
import { usePatientIntakeStatusContext } from '../hooks';

const USERS_WITH_ACCESS_TO_DOCUMENT_UPLOAD: UserType[] = [USER_TYPES.NURSE, USER_TYPES.DOCTOR, USER_TYPES.DIRECTOR];

type DocumentViewerProps = {
  fileUploadDocId: string;
  patientId: string;
  type: React.ComponentProps<typeof SingleDocumentListItem>['type'];
};

const DocumentViewer = ({ fileUploadDocId, patientId, type }: DocumentViewerProps) => {
  const { userType, user } = useAuth();

  if (getUserTypeValidity({ userType, validUsers: USERS_WITH_ACCESS_TO_DOCUMENT_UPLOAD }) || user?.uid === patientId) {
    return <SingleDocumentListItem documentId={fileUploadDocId} patientId={patientId} type={type} />;
  }

  return (
    <Stack direction="column" alignItems="center" gap={1} textAlign="center">
      <AssignmentTurnedInIcon fontSize="large" color="primary" />
      <Typography variant="body1">A document has been uploaded for this step</Typography>
    </Stack>
  );
};

type BaseDocumentStep = Extract<
  PatientIntakeStep,
  { currentStatus: { metadata: { fileUploadDocId: string } | null } | null }
>;

type ViewDocumentModalProps = {
  step: BaseDocumentStep;
  type: FileUploadType;
  isOpen: boolean;
  onClose: () => void;
};

export const ViewDocumentModal: React.FC<ViewDocumentModalProps> = ({ step, type, isOpen, onClose }) => {
  const { patientId, isHealthcareProvider } = usePatientIntakeStatusContext();

  const { metadata } = step.currentStatus ?? {};

  if (!isOpen) {
    return null;
  }

  if (!metadata || !('fileUploadDocId' in metadata)) {
    if (!isHealthcareProvider) {
      return <Navigate to="/account-details/documents" />;
    }

    return (
      <Dialog open={isOpen} onClose={onClose}>
        <DialogContent>
          <Typography variant="body1">
            Please view in the patient&apos;s page{' '}
            <Link component={RRLink} to={`/patients/${patientId}`}>
              here
            </Link>
            .
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const { fileUploadDocId } = metadata;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <DocumentViewer fileUploadDocId={fileUploadDocId} patientId={patientId} type={type} />
      </DialogContent>
    </Dialog>
  );
};
